import { useInView } from "@/hooks/useInView";

const pillars = [
  {
    label: "STRENGTH",
    desc: "Build physical capability, discipline, resilience, and trust in your body.",
  },
  {
    label: "SELF-DEFENSE",
    desc: "Develop awareness, boundaries, defensive positioning, practical responses, and the confidence to protect yourself responsibly.",
  },
  {
    label: "STRIKING MINDSET",
    desc: "Learn to respond to pressure with precision, composure, intention, and decisive action.",
  },
];

const frameworks = [
  {
    name: "ARRP",
    expansion: "Assess \u2022 Reflect \u2022 Respond \u2022 Proceed",
    desc: "A decision-making framework that helps people recognize what is happening, regulate their response, choose an appropriate action, and move forward with purpose.",
  },
  {
    name: "DCAE",
    expansion: "Defend \u2022 Counter \u2022 Attack \u2022 Evade",
    desc: "A practical physical-response framework that organizes self-defense and striking decisions according to the situation.",
  },
  {
    name: "4D",
    expansion: "Display \u2022 Deconstruct \u2022 Drill \u2022 Demonstrate",
    desc: "The instructional method used to teach skills clearly, break them down, practice them progressively, and demonstrate understanding.",
  },
];

export default function MethodSection() {
  const [ref, inView] = useInView();

  return (
    <section id="method" data-testid="method-section" className="bg-[#050505] py-16 sm:py-24" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Pillars */}
        <p className={`text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] font-body mb-4 reveal ${inView ? "visible" : ""}`}>
          What 3SM Means
        </p>
        <h2 className={`font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#F2EFE9] mb-6 reveal ${inView ? "visible" : ""} stagger-1`}>
          Strength. Self-Defense. Striking Mindset.
        </h2>
        <p className={`text-[#8C8C8C] font-body leading-relaxed max-w-3xl mb-12 reveal ${inView ? "visible" : ""} stagger-2`}>
          3SM stands for Strength, Self-Defense, and Striking Mindset. It is a structured training system that develops the physical skills, mental awareness, emotional composure, and responsible decision-making needed to navigate pressure in training and everyday life.
        </p>

        <div className="grid md:grid-cols-3 gap-6 mb-20">
          {pillars.map((p, i) => (
            <div
              key={p.label}
              data-testid={`pillar-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`glass-card border border-white/10 rounded-lg p-8 hover:border-[#D4AF37]/40 transition-all reveal ${inView ? "visible" : ""} stagger-${i + 2}`}
            >
              <p className="font-heading text-sm tracking-[0.18em] text-[#D4AF37] font-bold mb-4">
                {p.label}
              </p>
              <p className="text-[#F2EFE9] font-body text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>

        {/* Frameworks */}
        <div className="border-t border-white/10 pt-16">
          <p className={`text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] font-body mb-4 reveal ${inView ? "visible" : ""}`}>
            The System
          </p>
          <h2 className={`font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#F2EFE9] mb-4 reveal ${inView ? "visible" : ""} stagger-1`}>
            The 3SM Method.
          </h2>
          <p className={`text-[#8C8C8C] font-body text-sm leading-relaxed max-w-2xl mb-12 reveal ${inView ? "visible" : ""} stagger-2`}>
            Three proprietary frameworks that make 3SM a real, repeatable system, not a set of drills.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {frameworks.map((f, i) => (
              <div
                key={f.name}
                data-testid={`framework-${f.name.toLowerCase()}`}
                className={`border border-[#D4AF37]/20 rounded-lg p-8 bg-[#111111] hover:bg-[#161616] transition-all reveal ${inView ? "visible" : ""} stagger-${i + 2}`}
              >
                <p className="font-heading text-4xl font-black text-[#D4AF37] tracking-tight mb-2">
                  {f.name}
                </p>
                <p className="text-xs tracking-[0.15em] uppercase text-[#F2EFE9] font-body font-semibold mb-4">
                  {f.expansion}
                </p>
                <p className="text-sm text-[#8C8C8C] font-body leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
